import React from "react";
import { Link } from "react-router-dom";
import { Avatar, Badge, Btn, InsightBox, Overline } from "../../../ForBrands/ui/BrandUI";
import { MobileStackHeader } from "../../../ForBrands/ui/mobile/MobileShell";
import { ContextStrip, ListRule, MobileCard, MobilePageHead, StickyActionBar } from "../../../ForBrands/ui/mobile/MobileUI";
import { HELP_ACTION } from "../../../ForBrands/ui/mobile/support";
import { fmtMoney } from "../athleteUtils";
import { fmtDate, fmtLongRange } from "../../../ForBrands/brandDealUtils";

// Athlete Mode · offer view on phones (Figma 2000:989 "06 · Athlete offer
// view"). The page keeps the data, state and handlers; this is only the
// phone tree: stack header, context strip, one card per term, the Cache AI
// comparison and the sticky Accept / Counter / Ask / Decline bar.

// Every deal-flow stack screen (offer, counter, contract): the header with
// back and Help, an optional full-bleed context strip, the body with the
// 24px gutter, and the sticky action bar pinned to the bottom of short
// screens. The shell draws no chrome on these routes.
// `modal` is the page's open dialog (it renders as a sheet on phones).
export const DealScreen = ({ title, onBack, backLabel, strip, actions, modal, children }) => (
  <div className="flex min-h-dvh w-full min-w-0 flex-col bg-white">
    <MobileStackHeader leading="back" onLeading={onBack} leadingLabel={backLabel} title={title} action={HELP_ACTION} />
    {strip}
    <div className="flex w-full flex-1 flex-col px-6">
      <div className="flex flex-col gap-[18px] pb-8 pt-5">{children}</div>
      {actions && <StickyActionBar>{actions}</StickyActionBar>}
    </div>
    {modal}
  </div>
);

// A card led by an overline (Deliverables list, Exclusivity window, ...).
export const OverlineCard = ({ label, gap = "gap-2", tone, className = "", children }) => (
  <MobileCard tone={tone} className={className}>
    <div className={`flex flex-col ${gap}`}>
      <Overline>{label}</Overline>
      {children}
    </div>
  </MobileCard>
);

// A proposed term with its change badge on the right (the counter's
// Summary card and "Your counter" here share the look).
export const DiffRow = ({ label, value, badge }) => (
  <div className="flex items-center gap-3">
    <div className="flex min-w-0 flex-1 flex-col gap-[2px] break-words">
      <span className="bm-body-s text-bm-muted">{label}</span>
      <span className="bm-label-l text-bm-ink">{value}</span>
    </div>
    {badge}
  </div>
);

// Guardian notice for minors (2024:1574, 2024:1625): a subtle card with the
// dark badge above the text.
export const GuardianCard = ({ badge, children }) => (
  <MobileCard tone="subtle" className="items-start">
    {badge}
    <div className="bm-body-s text-bm-muted">{children}</div>
  </MobileCard>
);

const OfferMobile = ({
  onBack,
  stateLabel,
  termsTitle,
  subtitle,
  exclusivityTitle,
  brandLine,
  deal,
  campaign,
  brand,
  athlete,
  guardian,
  rep,
  comparison,
  exclusivityText,
  open,
  countered,
  busy,
  onAccept,
  onCounter,
  onAsk,
  onDecline,
  modal,
}) => {
  const offer = deal.offer;
  const counter = deal.counter;
  const range = fmtLongRange(offer.startDate, offer.endDate);

  const actions = (
    <>
      {open && (
        <Btn size="lg" className="w-full" onClick={onAccept} disabled={busy}>
          {busy ? "Working..." : "Accept offer"}
        </Btn>
      )}
      <div className="flex gap-[10px]">
        <Btn size="lg" variant="secondary" className="min-w-0 flex-1 !px-2" onClick={onCounter} disabled={busy}>
          {open ? "Counter offer" : "Revise counter"}
        </Btn>
        <Btn size="lg" variant="ghost" className="min-w-0 flex-1 !px-2" onClick={onAsk} disabled={busy}>
          {open ? "Ask a question" : "Message the brand"}
        </Btn>
      </div>
      <Btn variant="danger" className="w-full" onClick={onDecline} disabled={busy}>
        Decline
      </Btn>
    </>
  );

  return (
    <DealScreen
      title={stateLabel}
      onBack={onBack}
      strip={<ContextStrip title={`${campaign?.name || ""} · ${stateLabel}`} meta={`${brand.company} · ${range}`} />}
      actions={actions}
      modal={modal}
    >
      <MobilePageHead eyebrow="Deal flow" eyebrowBrand title={termsTitle} sub={subtitle} />

      {athlete.isMinor && (
        <GuardianCard badge={<Badge tone="dark" className="uppercase">Guardian required</Badge>}>
          Under 18 · A parent or legal guardian's signature is required to co-sign this contract.{" "}
          {guardian ? `${guardian.name} will be asked to co-sign after you.` : "Add a parent or guardian to your Circle before signing."}
          {!guardian && (
            <>
              {" "}
              <Link to="/athlete/circle" className="text-bm-orange underline underline-offset-2">
                Open My Circle
              </Link>
            </>
          )}
        </GuardianCard>
      )}

      <MobileCard>
        <div className="flex items-center gap-3">
          <Avatar name={brand.company} size={40} tone="muted" square className="!text-[12px] !font-semibold" />
          <div className="flex min-w-0 flex-1 flex-col gap-[2px] break-words">
            <h2 className="bm-h-xs text-bm-ink">{brand.company}</h2>
            <p className="bm-body-xs text-bm-muted">{brandLine}</p>
          </div>
          {brand.verified ? (
            <Badge tone="outline" dot="#1f9d55">
              Verified brand
            </Badge>
          ) : (
            <Badge tone="passive">New to Cache</Badge>
          )}
        </div>
        <ListRule />
        <div className="flex flex-col gap-[2px]">
          <Overline>Guaranteed compensation</Overline>
          <span className="bm-num-xl text-bm-ink">{fmtMoney(offer.compensation, { cents: true })}</span>
          <span className="bm-body-xs text-bm-muted">You earn the full amount. The brand pays all Cache and transfer fees.</span>
        </div>
      </MobileCard>

      <OverlineCard label="Deliverables list" gap="gap-3">
        {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- VoiceOver drops the list role of a list with no bullets */}
        <ul role="list" className="flex flex-col gap-2">
          {(offer.deliverableLines || []).map((line) => (
            <li key={line} className="flex items-start gap-2 bm-body-m text-bm-ink">
              <span aria-hidden="true" className="mt-[7px] h-[5px] w-[5px] shrink-0 rounded-full bg-bm-ink" />
              {line}
            </li>
          ))}
        </ul>
      </OverlineCard>

      <OverlineCard label="Campaign timeline & payments" gap="gap-3">
        <div className="flex flex-col gap-1">
          <span className="bm-body-m text-bm-ink">{range}</span>
          {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- VoiceOver drops the list role of a list with no bullets */}
          <ul role="list" className="flex flex-col gap-1">
            {(offer.milestones || []).map((m) => (
              <li key={m.id} className="bm-body-m text-bm-muted">
                {fmtMoney(m.amount)} {m.label}
              </li>
            ))}
          </ul>
        </div>
      </OverlineCard>

      <OverlineCard label="Exclusivity window">
        <span className="bm-h-xs text-bm-ink">{exclusivityTitle}</span>
        <span className="bm-body-s text-bm-muted">{exclusivityText}</span>
      </OverlineCard>

      {countered && counter && (
        <OverlineCard label="Your counter" gap="gap-3">
          <DiffRow
            label="Compensation"
            value={fmtMoney(counter.compensation, { cents: true })}
            badge={deal.counterDiff?.compensation && <Badge tone="outline">{deal.counterDiff.compensation}</Badge>}
          />
          <DiffRow
            label="Exclusivity"
            value={`${counter.exclusivityDays} days`}
            badge={deal.counterDiff?.exclusivity && <Badge tone="dark">{deal.counterDiff.exclusivity}</Badge>}
          />
          <DiffRow label="Timeline" value={`${fmtDate(counter.startDate, { year: false })} – ${fmtDate(counter.endDate)}`} />
          {counter.note && <p className="bm-body-s text-bm-muted">“{counter.note}”</p>}
        </OverlineCard>
      )}

      <InsightBox compact title="Cache AI comparison">
        {comparison}
      </InsightBox>

      {rep && <p className="bm-body-xs text-bm-muted">{rep.name} (your rep) has access to this thread and all deal terms.</p>}
    </DealScreen>
  );
};

export default OfferMobile;
